// System Design fundamentals, part 3: service architecture, reliability,
// observability, security and data pipelines.

export const systemDesignCore3 = [
  {
    slug: 'microservices-and-communication',
    title: 'Monoliths, Microservices and How Services Talk',
    summary: 'When to split a system, synchronous vs asynchronous communication, and keeping data consistent across services.',
    readTime: 11,
    content: [
      { type: 'h2', text: 'Why this matters' },
      { type: 'p', text: 'Many designs eventually involve several services. Splitting a system well makes teams faster; splitting it badly creates a slow, fragile “distributed monolith”. Interviewers want to see that you split for real reasons and understand the costs.' },

      { type: 'h2', text: 'The mental model: departments in a company' },
      { type: 'p', text: 'A small startup works in one room and talks freely. As it grows, it forms departments with clear responsibilities that communicate through agreed channels. Splitting too early adds meetings and paperwork for no benefit; never splitting means everyone trips over everyone else.' },

      { type: 'h2', text: 'Monolith vs microservices' },
      { type: 'table', headers: ['', 'Modular monolith', 'Microservices'], rows: [
        ['Deploy', 'One unit', 'Many independent units'],
        ['Data', 'One database, real transactions', 'A database per service, eventual consistency'],
        ['Calls between parts', 'In-process function calls', 'Network calls that can be slow or fail'],
        ['Debugging', 'One log and stack trace', 'Distributed tracing across services'],
        ['Best when', 'Small to medium teams, early products', 'Many teams, parts with very different scaling or reliability needs']
      ] },
      { type: 'callout', variant: 'tip', title: 'A good default answer', text: 'Start with a well-structured modular monolith organised by business domain. Extract a service when there is a concrete reason: a team needs independent deploys, a component needs very different scaling, or a failure must be isolated.' },

      { type: 'h2', text: 'Synchronous vs asynchronous communication' },
      { type: 'table', headers: ['', 'Synchronous (HTTP/gRPC)', 'Asynchronous (events, queues)'], rows: [
        ['Use when', 'The caller needs an answer now', 'Something happened that others may react to'],
        ['Coupling', 'Caller depends on callee being up', 'Producer does not wait for consumers'],
        ['Failure impact', 'Chains of calls multiply failure risk', 'Consumers can catch up later'],
        ['Example', 'Checkout asks pricing for the final price', '“Order placed” triggers email, analytics, loyalty points']
      ] },
      { type: 'p', text: 'Long chains of synchronous calls are fragile: if each of five services is up 99.9% of the time, the chain is up roughly 99.5% of the time, and latencies add up. Prefer events for anything that does not need an immediate answer.' },

      { type: 'h2', text: 'Data consistency across services: sagas' },
      { type: 'p', text: 'Without a shared database there is no single transaction across services. A saga breaks a business process into local steps, each with a compensating action that undoes it if a later step fails.' },
      { type: 'code', language: 'text', code: `Place order saga
  1. Orders:    create order (pending)          ↺ cancel order
  2. Payments:  charge customer                 ↺ refund
  3. Inventory: reserve items                   ↺ release items
  4. Orders:    mark order confirmed

If step 3 fails → refund payment → cancel order` },
      { type: 'p', text: 'A saga can be coordinated by an orchestrator that tells each service what to do, or by choreography where each service reacts to the previous service’s events. Orchestration is easier to follow for complex flows.' },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Splitting into many services for a small team.',
        'Services sharing one database, so they cannot change independently.',
        'Long synchronous call chains on the critical path.',
        'Assuming distributed transactions will just work.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'How do you split a monolith into services?', answer: ['Gradually. Identify a well-bounded domain with a clear reason to split, put an interface in front of it inside the monolith, route traffic for that capability to a new service, migrate its data, then remove the old code. Repeat only where it pays off.'] },
      { type: 'qa', question: 'How does one service get data owned by another?', answer: ['Call its API when you need fresh data, or keep a local read-only copy updated from its events when you need fast reads and can tolerate slight staleness. Never read another service’s database directly.'] },

      { type: 'exercise', title: 'Find the sync calls to remove', task: 'Checkout currently calls, in order: pricing, inventory, payment, email, analytics, loyalty, recommendations. Which calls must stay synchronous, and which should become events?', solution: `Keep synchronous (the user needs the result to complete checkout):
  pricing, inventory reservation, payment
Make asynchronous via an "order.placed" event:
  email, analytics, loyalty points, recommendation updates
Result: shorter checkout latency, and an outage in email or
analytics no longer blocks orders`, language: 'text' },

      { type: 'summary', items: [
        'Start with a modular monolith; split for concrete reasons.',
        'Use synchronous calls when an answer is needed now, events otherwise.',
        'Each service owns its data; use sagas for multi-service processes.',
        'Long synchronous chains multiply latency and failure risk.'
      ] }
    ]
  },
  {
    slug: 'reliability-patterns',
    title: 'Reliability Patterns: Timeouts, Retries and Circuit Breakers',
    summary: 'Designing for failure: redundancy, timeouts, safe retries, circuit breakers, bulkheads and graceful degradation.',
    readTime: 11,
    content: [
      { type: 'h2', text: 'Why this matters' },
      { type: 'p', text: 'In a system with many machines and dependencies, something is always failing somewhere. Reliable systems are not those where nothing fails; they are those where failures stay small. Interviewers expect you to discuss what happens when each component in your design goes down.' },

      { type: 'h2', text: 'The mental model: fuses and fire doors' },
      { type: 'p', text: 'A building limits damage with fuses that cut power to one faulty circuit and fire doors that stop a fire spreading between rooms. Reliability patterns do the same for software: contain a failure where it starts.' },

      { type: 'h2', text: 'Redundancy first' },
      { type: 'ul', items: [
        'Run several instances of every service across availability zones.',
        'Replicate databases and automate failover.',
        'Make load balancers and caches redundant too.',
        'Remember the maths: two independent 99% components in parallel are about 99.99% available together, but five 99.9% components in a chain are only about 99.5%.'
      ] },

      { type: 'h2', text: 'Timeouts' },
      { type: 'p', text: 'Every network call needs a timeout. Without one, a slow dependency ties up threads and connections until your own service runs out of capacity and fails too. Set timeouts based on what the dependency normally needs, not on how long a user might tolerate.' },

      { type: 'h2', text: 'Retries done safely' },
      { type: 'code', variants: [
        { language: 'java', code: `import java.util.ArrayList;
import java.util.List;

public class Backoff {
    static List<Long> backoffDelays(int attempts, long baseMs, long maxMs) {
        List<Long> delays = new ArrayList<>();
        for (int i = 0; i < attempts; i++) delays.add(Math.min(maxMs, baseMs * (1L << i)));
        return delays;
    }

    public static void main(String[] args) {
        System.out.println(backoffDelays(6, 100, 2000));
    }
}`, output: `[100, 200, 400, 800, 1600, 2000]` },
        { language: 'python', code: `def backoff_delays(attempts, base_ms=100, max_ms=2000):
    return [min(max_ms, base_ms * 2 ** i) for i in range(attempts)]

print(backoff_delays(6))`, output: `[100, 200, 400, 800, 1600, 2000]` },
        { language: 'javascript', code: `function backoffDelays(attempts, baseMs = 100, maxMs = 2000) {
  return Array.from({ length: attempts }, (_, i) => Math.min(maxMs, baseMs * 2 ** i));
}

console.log(backoffDelays(6));`, output: `[ 100, 200, 400, 800, 1600, 2000 ]` }
      ] },
      { type: 'ul', items: [
        'Retry only errors that may succeed next time: timeouts, 429, 503. Never retry validation errors.',
        'Use exponential backoff with a cap, and add random jitter so clients do not retry in lockstep.',
        'Limit attempts and total time.',
        'Only retry idempotent operations, or use idempotency keys for writes such as payments.'
      ] },

      { type: 'h2', text: 'Circuit breakers' },
      { type: 'p', text: 'When a dependency keeps failing, retrying makes things worse for everyone. A circuit breaker tracks failures; after too many, it “opens” and fails calls immediately for a while, then lets a few test calls through (“half-open”). If they succeed, it closes again.' },
      { type: 'table', headers: ['State', 'Behaviour'], rows: [
        ['Closed', 'Calls go through; failures are counted'],
        ['Open', 'Calls fail immediately (or use a fallback) for a cooldown period'],
        ['Half-open', 'A few test calls go through; success closes, failure reopens']
      ] },

      { type: 'h2', text: 'Bulkheads and graceful degradation' },
      { type: 'ul', items: [
        '**Bulkheads**: give each dependency its own connection pool or concurrency limit, so a slow recommendations service cannot consume the capacity checkout needs.',
        '**Fallbacks**: serve cached data, defaults or a reduced experience instead of an error.',
        '**Load shedding**: under extreme load, reject low-priority requests early to keep critical ones working.',
        '**Feature flags**: switch off non-essential features during incidents.'
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'No timeouts on HTTP clients and database calls.',
        'Immediate retries in a tight loop, multiplying load during an outage.',
        'Retrying non-idempotent operations and creating duplicates.',
        'Every dependency treated as critical, so any failure becomes a full outage.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'What is a retry storm?', answer: ['When a service struggles, its clients retry, multiplying traffic, which makes it struggle more, which causes more retries. Exponential backoff with jitter, retry limits and circuit breakers prevent it.'] },
      { type: 'qa', question: 'What should happen if the recommendations service is down on a product page?', answer: ['The product page should still load. Use a short timeout, a circuit breaker, and a fallback such as popular items from a cache, or simply hide the section.'] },

      { type: 'exercise', title: 'Harden a checkout flow', task: 'Checkout calls a payment provider that occasionally times out or returns 503. Describe timeouts, retries, idempotency and what the user sees, so customers are never charged twice.', solution: `- Timeout per payment call (e.g. 5–10 s, based on the provider's normal latency)
- Every attempt uses the same idempotency key for this order
- Retry 503/timeouts up to 2–3 times with exponential backoff + jitter
- If the outcome is unknown, mark the payment "pending", check the
  provider's status (or wait for its webhook) before retrying further
- User sees "Confirming your payment…" rather than an error,
  then success or a clear failure with no double charge`, language: 'text' },

      { type: 'summary', items: [
        'Assume failure: redundancy across zones for every component.',
        'Every call needs a timeout; retries need backoff, jitter and limits.',
        'Circuit breakers and bulkheads stop failures spreading.',
        'Degrade gracefully instead of failing completely.'
      ] }
    ]
  },
  {
    slug: 'observability',
    title: 'Observability: Metrics, Logs, Traces and SLOs',
    summary: 'Knowing what your system is doing in production, alerting on what users feel, and debugging across services.',
    readTime: 9,
    content: [
      { type: 'h2', text: 'Why this matters' },
      { type: 'p', text: 'A design is only as good as your ability to run it. When latency spikes at 2 a.m., you need to know quickly what is wrong and where. Mentioning how you would monitor a system is a mark of senior thinking in interviews.' },

      { type: 'h2', text: 'The mental model: a car dashboard, a trip log and a GPS trail' },
      { type: 'p', text: 'Metrics are the dashboard: speed, fuel, temperature at a glance. Logs are the detailed trip log of individual events. Traces are the GPS trail showing exactly which roads one journey took and how long each stretch was.' },

      { type: 'h2', text: 'The three pillars' },
      { type: 'table', headers: ['Signal', 'What it is', 'Use it for'], rows: [
        ['Metrics', 'Numbers over time: request rate, error rate, latency percentiles, CPU, queue length', 'Dashboards and alerts'],
        ['Logs', 'Structured records of individual events, with context', 'Understanding what happened to a specific request'],
        ['Traces', 'The path of one request across services, with timings per step', 'Finding which service or call is slow']
      ] },
      { type: 'tip', text: 'Give every request a correlation (trace) ID at the edge and pass it through every service, queue message and log line. It turns “something failed somewhere” into a single searchable thread.' },

      { type: 'h2', text: 'What to measure' },
      { type: 'ul', items: [
        '**Rate, errors, duration** for every service and endpoint.',
        'Latency as **percentiles** (p50, p95, p99), not averages, which hide slow requests.',
        '**Saturation**: CPU, memory, connection pools, queue lengths and age of the oldest message.',
        'Business metrics: sign-ups, orders, payments succeeded, which often reveal problems first.'
      ] },

      { type: 'h2', text: 'SLIs, SLOs and error budgets' },
      { type: 'p', text: 'A service level indicator (SLI) is something you measure, such as “percentage of checkout requests that succeed in under 1 second”. A service level objective (SLO) is the target, such as 99.9% over 30 days. The error budget is what is left: 0.1% of requests may fail. While budget remains, teams ship quickly; when it runs out, reliability work takes priority.' },
      { type: 'code', variants: [
        { language: 'java', code: `import java.util.Locale;

public class ErrorBudget {
    public static void main(String[] args) {
        int minutesIn30Days = 30 * 24 * 60;
        for (String slo : new String[] {"99", "99.9", "99.99"}) {
            double allowed = minutesIn30Days * (1 - Double.parseDouble(slo) / 100);
            System.out.printf(Locale.ROOT, "%s%% → %.1f minutes of downtime per 30 days%n", slo, allowed);
        }
    }
}`, output: `99% → 432.0 minutes of downtime per 30 days
99.9% → 43.2 minutes of downtime per 30 days
99.99% → 4.3 minutes of downtime per 30 days` },
        { language: 'python', code: `minutes_in_30_days = 30 * 24 * 60
for slo in (99, 99.9, 99.99):
    allowed = minutes_in_30_days * (1 - slo / 100)
    print(f"{slo}% → {allowed:.1f} minutes of downtime per 30 days")`, output: `99% → 432.0 minutes of downtime per 30 days
99.9% → 43.2 minutes of downtime per 30 days
99.99% → 4.3 minutes of downtime per 30 days` },
        { language: 'javascript', code: `const minutesIn30Days = 30 * 24 * 60;
for (const slo of [99, 99.9, 99.99]) {
  const allowed = minutesIn30Days * (1 - slo / 100);
  console.log(\`\${slo}% → \${allowed.toFixed(1)} minutes of downtime per 30 days\`);
}`, output: `99% → 432.0 minutes of downtime per 30 days
99.9% → 43.2 minutes of downtime per 30 days
99.99% → 4.3 minutes of downtime per 30 days` }
      ] },

      { type: 'h2', text: 'Alerting that people trust' },
      { type: 'ul', items: [
        'Alert on symptoms users feel (error rate, latency, failed checkouts), not on every high CPU reading.',
        'Every alert should be actionable and link to a runbook.',
        'Too many noisy alerts train people to ignore them.'
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Watching average latency instead of p99.',
        'Unstructured logs that cannot be searched or correlated.',
        'No way to follow one request across services.',
        'Alerting on causes nobody can act on.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'How would you monitor the system you just designed?', answer: ['Dashboards with request rate, error rate and p50/p95/p99 latency per service; saturation metrics for databases, caches and queues; distributed tracing with correlation IDs; SLO-based alerts on the user-facing paths; and business metrics such as successful orders per minute.'] },
      { type: 'qa', question: 'Why are percentiles better than averages for latency?', answer: ['Averages hide the slow tail. If most requests take 50 ms and 1% take 5 seconds, the average still looks fine, but many users are suffering. p99 shows the experience of the slowest 1%.'] },

      { type: 'exercise', title: 'Define SLOs', task: 'Define one SLI and SLO each for: a login API, a search API, and an email-sending background job. Then suggest one alert.', solution: `Login API   SLI: % of logins returning non-5xx within 800 ms
            SLO: 99.9% over 30 days
Search API  SLI: % of searches returning within 300 ms
            SLO: 99% over 30 days
Email job   SLI: % of emails sent within 5 minutes of being queued
            SLO: 99.5% over 30 days
Alert: page on-call when the login error budget is burning
       fast enough to run out within a few hours`, language: 'text' },

      { type: 'summary', items: [
        'Metrics for trends and alerts, logs for details, traces for request paths.',
        'Use percentiles and correlation IDs.',
        'SLOs and error budgets turn reliability into a clear target.',
        'Alert on what users feel, and make every alert actionable.'
      ] }
    ]
  },
  {
    slug: 'security-in-system-design',
    title: 'Security in System Design',
    summary: 'Authentication, authorisation, protecting data in transit and at rest, secrets, and abuse prevention.',
    readTime: 10,
    content: [
      { type: 'h2', text: 'Why this matters' },
      { type: 'p', text: 'Security is rarely the main topic of a design interview, but ignoring it is a red flag. A few well-placed points (who can access what, how data is protected, how abuse is limited) show maturity and often prevent the most damaging real-world incidents.' },

      { type: 'h2', text: 'The mental model: a building with layers' },
      { type: 'p', text: 'A secure building has a guarded entrance (authentication), key cards that open only certain rooms (authorisation), locked cabinets (encryption at rest), sealed envelopes between offices (encryption in transit), and cameras (audit logs). No single layer is enough; together they make breaking in hard and detectable.' },

      { type: 'h2', text: 'Authentication' },
      { type: 'ul', items: [
        'Use proven identity providers or standards (OAuth 2.0 / OpenID Connect) instead of inventing your own.',
        'Hash passwords with slow, salted algorithms such as Argon2 or bcrypt; never store them in plain text.',
        'Short-lived access tokens plus refresh tokens; store web session tokens in secure, HTTP-only cookies.',
        'Offer multi-factor authentication for sensitive accounts and actions.'
      ] },

      { type: 'h2', text: 'Authorisation' },
      { type: 'p', text: 'Knowing who someone is does not mean they may access a particular record. The most common serious vulnerability in real APIs is broken object-level authorisation: changing an ID in a URL and seeing someone else’s data.' },
      { type: 'compare', badLabel: 'Checks login only', bad: `GET /invoices/:id
→ SELECT * FROM invoices WHERE id = :id`, goodLabel: 'Checks ownership', good: `GET /invoices/:id
→ SELECT * FROM invoices
  WHERE id = :id AND account_id = :callerAccountId
→ 404 if no row`, text: 'Scope every query by the caller’s identity or tenant, and deny by default.' },
      { type: 'ul', items: [
        'Role-based access control (admin, instructor, learner) for coarse permissions.',
        'Ownership or attribute checks for individual records.',
        'Enforce on the server; hiding buttons in the UI is not security.'
      ] },

      { type: 'h2', text: 'Protecting data' },
      { type: 'table', headers: ['Concern', 'Practice'], rows: [
        ['In transit', 'TLS everywhere, including between internal services where possible'],
        ['At rest', 'Encrypted disks and backups; extra field-level encryption for very sensitive data'],
        ['Secrets', 'A secrets manager or environment configuration, rotated regularly; never in code'],
        ['Payments', 'Let a payment provider handle card data so it never touches your servers'],
        ['Personal data', 'Collect only what you need, restrict access, support deletion requests']
      ] },

      { type: 'h2', text: 'Abuse prevention' },
      { type: 'ul', items: [
        'Rate limits on login, OTP, sign-up and expensive endpoints.',
        'Validate and size-limit all input; parameterised queries prevent injection.',
        'Signed, expiring URLs for private files.',
        'Audit logs for sensitive actions such as permission changes and data exports.'
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Trusting a user ID sent in the request body.',
        'Checking authentication but not ownership of the specific record.',
        'Secrets committed to repositories or printed in logs.',
        'No rate limits on authentication endpoints.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'How would you secure a multi-tenant SaaS application?', answer: ['Every request carries a tenant derived from the authenticated user, every query is scoped by tenant ID (enforced in a shared data-access layer or with database row-level security), tenant data is never cached under keys without the tenant ID, and automated tests check that one tenant cannot read another’s data.'] },
      { type: 'qa', question: 'Where should tokens be stored in a web app?', answer: ['Prefer secure, HTTP-only, SameSite cookies for session tokens, which scripts cannot read. Tokens in local storage are exposed to any successful cross-site scripting attack.'] },

      { type: 'exercise', title: 'Review a design', task: 'A course platform lets instructors upload videos and learners watch them after paying. List five security controls you would add.', solution: `1. Authorisation: only enrolled learners (or the course's instructors)
   can fetch lesson video URLs; checked on the server per request
2. Signed, short-lived CDN URLs for videos
3. Instructor upload via pre-signed URLs with size/type limits,
   then malware scanning before publishing
4. Payments through a provider (no card data stored), webhooks verified
   by signature
5. Rate limits on login/OTP, MFA for instructor and admin accounts,
   audit logs for price and permission changes`, language: 'text' },

      { type: 'summary', items: [
        'Authenticate with proven standards; hash passwords properly.',
        'Authorise every record access on the server, scoped by owner or tenant.',
        'Encrypt in transit and at rest; keep secrets out of code.',
        'Rate-limit sensitive endpoints and log sensitive actions.'
      ] }
    ]
  },
  {
    slug: 'data-pipelines-and-analytics',
    title: 'Data Pipelines, Batch and Stream Processing',
    summary: 'Separating analytics from production, batch vs streaming, aggregating events at scale, and choosing analytical stores.',
    readTime: 9,
    content: [
      { type: 'h2', text: 'Why this matters' },
      { type: 'p', text: 'View counts, dashboards, recommendations, fraud detection and reports all depend on processing large volumes of events. Running those queries on your production database slows down the product. Many design questions include an analytics part, and the right answer is usually a separate pipeline.' },

      { type: 'h2', text: 'The mental model: the shop till and the accounts department' },
      { type: 'p', text: 'The till (production database) must be fast for each sale. Nobody runs yearly reports on the till during rush hour. Instead, sales records are copied to the accounts department (an analytics system), which crunches numbers without slowing the shop.' },

      { type: 'h2', text: 'Separate production and analytics' },
      { type: 'table', headers: ['', 'Production (OLTP)', 'Analytics (OLAP)'], rows: [
        ['Queries', 'Many small reads and writes by key', 'Few large scans and aggregations'],
        ['Storage layout', 'Row-oriented', 'Column-oriented (fast aggregation over few columns)'],
        ['Examples', 'Postgres, MySQL, DynamoDB', 'ClickHouse, BigQuery, Snowflake, Redshift'],
        ['Freshness', 'Real time', 'Seconds to hours, depending on the pipeline']
      ] },

      { type: 'h2', text: 'Batch vs stream processing' },
      { type: 'table', headers: ['', 'Batch', 'Stream'], rows: [
        ['How', 'Process accumulated data on a schedule', 'Process events continuously as they arrive'],
        ['Latency', 'Minutes to hours', 'Seconds'],
        ['Simplicity', 'Simpler, easy to rerun', 'More complex: state, late events, exactly-once concerns'],
        ['Use for', 'Daily reports, model training, billing', 'Live dashboards, fraud alerts, trending topics']
      ] },
      { type: 'tip', text: 'Many systems use both: a stream for fast, approximate numbers shown live, and a batch job that later recomputes exact numbers from the raw events.' },

      { type: 'h2', text: 'Aggregating events at scale' },
      { type: 'code', variants: [
        { language: 'java', code: `import java.util.*;

public class ViewCounts {
    record View(String videoId, String at) {}

    public static void main(String[] args) {
        // Count views per video per minute instead of writing every view to the database
        List<View> events = List.of(new View("a", "12:00:05"), new View("a", "12:00:40"),
                                    new View("b", "12:00:41"), new View("a", "12:01:02"));
        Map<String, Integer> counts = new LinkedHashMap<>();
        for (View e : events) {
            String minute = e.at().substring(0, 5);               // "12:00"
            counts.merge(e.videoId() + "@" + minute, 1, Integer::sum);
        }
        System.out.println(counts);
    }
}`, output: `{a@12:00=2, b@12:00=1, a@12:01=1}` },
        { language: 'python', code: `# Count views per video per minute instead of writing every view to the database
events = [
    {"video_id": "a", "at": "12:00:05"}, {"video_id": "a", "at": "12:00:40"},
    {"video_id": "b", "at": "12:00:41"}, {"video_id": "a", "at": "12:01:02"},
]

counts = {}
for e in events:
    minute = e["at"][:5]                                    # "12:00"
    key = f"{e['video_id']}@{minute}"
    counts[key] = counts.get(key, 0) + 1
print(counts)`, output: `{'a@12:00': 2, 'b@12:00': 1, 'a@12:01': 1}` },
        { language: 'javascript', code: `// Count views per video per minute instead of writing every view to the database
const events = [
  { videoId: "a", at: "12:00:05" }, { videoId: "a", at: "12:00:40" },
  { videoId: "b", at: "12:00:41" }, { videoId: "a", at: "12:01:02" }
];

const counts = {};
for (const e of events) {
  const minute = e.at.slice(0, 5);                 // "12:00"
  const key = \`\${e.videoId}@\${minute}\`;
  counts[key] = (counts[key] ?? 0) + 1;
}
console.log(counts);`, output: `{ 'a@12:00': 2, 'b@12:00': 1, 'a@12:01': 1 }` }
      ] },
      { type: 'p', text: 'Writing one aggregated row per video per minute instead of one row per view cuts writes by orders of magnitude, and keeps hot videos from turning one database row into a bottleneck.' },

      { type: 'h2', text: 'A typical pipeline' },
      { type: 'code', language: 'text', code: `Apps ─► Event stream (Kafka) ─┬─► Stream processor ─► live aggregates (Redis / OLAP)
                               └─► Raw event storage (object storage, partitioned by date)
                                          └─► Batch jobs ─► Data warehouse ─► Dashboards, ML` },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Running analytics queries on the production primary.',
        'Updating a counter row on every event.',
        'Discarding raw events, which makes fixing a bug in the numbers impossible.',
        'Ignoring late or duplicate events in streaming counts.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'How would you count video views accurately at very high volume?', answer: ['Publish view events to a stream, aggregate them per video per time window in a stream processor, and write aggregates to the analytics store. Keep the raw events, deduplicate obvious bots and repeated views, and run a periodic batch job that recomputes exact counts.'] },
      { type: 'qa', question: 'Why are column stores fast for analytics?', answer: ['They store each column together, so an aggregation over two columns reads only those columns instead of entire rows, and similar values compress very well.'] },

      { type: 'exercise', title: 'Design trending courses', task: 'Show a “trending this week” list of courses, updated every few minutes, based on enrolments and lesson views. Sketch the pipeline.', solution: `- Apps publish "enrolled" and "lesson_viewed" events to a stream
- A stream processor keeps rolling 7-day counts per course in time
  buckets (e.g. hourly), weighting enrolments more than views
- Every few minutes it writes the top 50 to Redis (sorted set)
- The website reads the cached list; no queries hit production
- Raw events are stored daily for recomputing and for analytics`, language: 'text' },

      { type: 'summary', items: [
        'Keep analytics off the production database.',
        'Batch for exact, periodic results; streams for fast, live results.',
        'Aggregate events into windows instead of writing every event.',
        'Keep raw events so numbers can be recomputed.'
      ] }
    ]
  }
];
